/* global VAPID_KEY */
import * as Sentry from '@sentry/react';
import { subscribeToPushNotifications, unsubscribeFromPushNotifications } from '@services/services';
import { getToken, MessagePayload, onMessage } from 'firebase/messaging';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { messaging } from '../config/firebase-push';
import { NOTIFICATION_EVENTS_MAP } from '../utils/notificationConstants';

// Type guard to check if error has a code property
const isErrorWithCode = (error: unknown): error is { code: string } => {
  return typeof error === 'object' && error !== null && 'code' in error;
};

interface PushNotificationContextProps {
  permission: string;
  fcmToken: string | null;
  requestPermission: () => Promise<void>;
  clearPushNotifications: () => Promise<void>;
}

const PushNotificationContext = createContext<PushNotificationContextProps>({
  permission: 'default',
  fcmToken: null,
  requestPermission: async () => {},
  clearPushNotifications: async () => {},
});

export const usePushNotificationContext = () => {
  return useContext(PushNotificationContext);
};

interface PushNotificationProviderProps {
  children: ReactNode;
}

export const PushNotificationProvider = ({ children }: PushNotificationProviderProps) => {
  const [permission, setPermission] = useState<string>('default');
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const navigate = useNavigate();

  const subscribeToTopic = async (token: string, topic: string) => {
    try {
      await subscribeToPushNotifications(token, topic);
    } catch (_error) {
      Sentry.captureException(_error, {
        level: 'error',
        tags: {
          feature: 'push_notifications',
          action: 'subscribe_to_topic_error',
        },
        extra: {
          idcomercio: localStorage.getItem('id_commerce'),
        },
      });
    }
  };

  const unsubscribeFromTopic = async (token: string, topic: string) => {
    try {
      await unsubscribeFromPushNotifications(token, topic);
    } catch (_error) {
      Sentry.captureException(_error, {
        level: 'error',
        tags: {
          feature: 'push_notifications',
          action: 'unsubscribe_from_topic_error',
        },
        extra: {
          idcomercio: localStorage.getItem('id_commerce'),
        },
      });
    }
  };

  const clearPushNotifications = async () => {
    if (fcmToken) {
      await unsubscribeFromTopic(fcmToken, localStorage.getItem('id_commerce') ?? '');
    }
  };

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Este navegador no soporta notificaciones.');
      return;
    }

    if (!messaging) {
      toast.error('Firebase Messaging no está disponible en este entorno.');
      return;
    }

    try {
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult === 'granted') {
        // Get the existing service worker registration
        const registration = await navigator.serviceWorker.ready;

        const currentToken = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration, // Explicitly use the existing SW
        });

        if (currentToken) {
          setFcmToken(currentToken);

          const commerceId = localStorage.getItem('id_commerce');

          if (commerceId) {
            await subscribeToTopic(currentToken, commerceId);
            toast.success('Notificaciones activadas correctamente');
          } else {
            toast.success('Notificaciones activadas, pero no se pudo suscribir al comercio (falta ID).');
          }
        }
      } else {
        toast.error('Permiso de notificaciones denegado.');
      }
    } catch (error: unknown) {
      Sentry.captureException(error, {
        level: 'error',
        tags: {
          feature: 'push_notifications',
          action: 'request_permission_error',
        },
        extra: {
          commerceId: localStorage.getItem('id_commerce') ?? null,
        },
      });

      // Handle Service Worker registration errors gracefully
      if (isErrorWithCode(error) && error.code === 'messaging/failed-service-worker-registration') {
        toast.error('Las notificaciones push funcionarán después de construir la aplicación para producción.');
      } else {
        toast.error('Error al habilitar notificaciones.');
      }
    }
  };

  // Initialize permission state and auto-subscribe if already granted
  useEffect(() => {
    if ('Notification' in window && messaging) {
      const currentPermission = Notification.permission;
      setPermission(currentPermission);

      if (currentPermission === 'granted' && !fcmToken) {
        (async () => {
          try {
            // Get the existing service worker registration
            const registration = await navigator.serviceWorker.ready;

            const currentToken = await getToken(messaging, {
              vapidKey: VAPID_KEY,
              serviceWorkerRegistration: registration, // Explicitly use the existing SW
            });

            if (currentToken) {
              setFcmToken(currentToken);

              const commerceId = localStorage.getItem('id_commerce');

              if (commerceId) {
                await subscribeToTopic(currentToken, commerceId);
              }
            }
          } catch (error: unknown) {
            if (isErrorWithCode(error) && error.code === 'messaging/failed-service-worker-registration') {
              // Silently ignore - expected in development
            } else {
              Sentry.captureException(error, {
                level: 'error',
                tags: {
                  feature: 'push_notifications',
                  action: 'get_token_error',
                },
                extra: {
                  error: error instanceof Error ? error.message : String(error),
                  stack: error instanceof Error ? error.stack : undefined,
                  idcomercio: localStorage.getItem('id_commerce'),
                },
              });
            }
          }
        })();
      }
    }
  }, []);

  // Listen for foreground messages
  useEffect(() => {
    if (!messaging) {
      return;
    }

    const unsubscribe = onMessage(messaging, (payload) => {
      // Force native notification even when app is in foreground
      if (Notification.permission === 'granted') {
        // Prioritize data payload (to avoid FCM auto-display duplicates)
        // If backend sends notification payload, FCM might show it automatically
        const title = payload.data?.title || payload.notification?.title || 'Nueva notificación';
        const body = payload.data?.body || payload.notification?.body || '';
        const icon = payload.data?.icon || payload.notification?.image || '/pwa-192x192.png';

        // Create native notification manually
        const notification = new Notification(title, {
          body: body,
          icon: icon,
          data: payload.data,
          tag: 'plick-notification', // Prevent duplicates with same tag
        });

        // Handle click on foreground notification
        notification.onclick = (event) => {
          event.preventDefault();
          notification.close();

          const data = payload.data;
          if (data) {
            if (data.eventId === NOTIFICATION_EVENTS_MAP.newOrder.key && data.orderId) {
              navigate(`/orders/${data.orderId}`);
            } else if (data.eventId === NOTIFICATION_EVENTS_MAP.changeOrderStatus.key && data.orderId) {
              navigate(`/orders/${data.orderId}`);
            }
          }
        };
      }
    });

    return () => {
      unsubscribe();
    };
  }, [navigate]); // Include navigate in dependencies

  // Handle notification clicks from Service Worker
  useEffect(() => {
    const handleNotificationClick = (payload: MessagePayload) => {
      const data = payload.data;
      if (!data) return;

      if (data.eventId === NOTIFICATION_EVENTS_MAP.newOrder.key && data.orderId) {
        navigate(`/orders/${data.orderId}`);
      } else if (data.eventId === NOTIFICATION_EVENTS_MAP.changeOrderStatus.key && data.orderId) {
        navigate(`/orders/${data.orderId}`);
      }
    };

    const messageHandler = (event: MessageEvent) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
        handleNotificationClick(event.data.payload);
      } else if (event.data && event.data.type === 'SENTRY_EVENT') {
        // Forward Sentry events from Service Worker to Sentry
        const { eventType, data } = event.data;
        if (eventType === 'error') {
          Sentry.captureException(new Error(data.message), {
            level: 'error',
            tags: data.tags,
            extra: data.extra,
          });
        }
        // Los eventos 'info' del Service Worker ya no se reenvían a Sentry (eran telemetría, no errores)
      }
    };

    navigator.serviceWorker.addEventListener('message', messageHandler);

    return () => {
      navigator.serviceWorker.removeEventListener('message', messageHandler);
    };
  }, [navigate]);

  const contextValue: PushNotificationContextProps = {
    permission,
    fcmToken,
    requestPermission,
    clearPushNotifications,
  };

  return <PushNotificationContext.Provider value={contextValue}>{children}</PushNotificationContext.Provider>;
};
